import { ErrorMessage } from '@hookform/error-message';
import { ReactComponent as IconView } from 'assets/icons/icon-view.svg';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import {
  Error,
  Input,
  InputWrapper,
  Label,
  PasswordToggle,
  Wrapper,
} from './text-input.styles';

interface Props extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  errors?: any;
  component?: 'input' | 'textarea';
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const TextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    { className, id: passedId, label, errors, type, component, ...inputProps },
    forwardedRef
  ) => {
    const [id, setId] = useState(passedId);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const isPasswordType = type === 'password';

    // Set a unique ID by default
    useEffect(() => {
      if (!passedId) {
        setId(`id${nanoid()}`); // ID should start with a letter for compatibility
      }
    }, [passedId]);

    const togglePasswordVisibility = () => setPasswordVisible((prev) => !prev);
    const errorId = `inline-error-${id}`;
    const ariaDescribedBy = inputProps['aria-describedby'];
    // If the field has an error, merge error with potential existing description
    const describedByIds =
      inputProps.name && errors && errors[inputProps.name]
        ? ariaDescribedBy
          ? `${errorId} ${ariaDescribedBy}`
          : errorId
        : ariaDescribedBy;

    return (
      <Wrapper className={className} hasIconPadding={isPasswordType}>
        {label && <Label htmlFor={id}>{label}</Label>}
        <InputWrapper>
          <Input
            id={id}
            ref={forwardedRef}
            {...(inputProps as any)}
            aria-describedby={describedByIds}
            type={
              isPasswordType ? (passwordVisible ? 'text' : 'password') : type
            }
            as={component}
          />
          {isPasswordType && (
            <PasswordToggle
              onClick={togglePasswordVisibility}
              aria-label='Show password'
              role='switch'
              aria-checked={passwordVisible}
              isActive={passwordVisible}
              type='button'
            >
              <IconView aria-hidden />
            </PasswordToggle>
          )}
        </InputWrapper>
        {inputProps.name && errors && (
          <ErrorMessage
            errors={errors}
            name={inputProps.name}
            render={(data) => <Error id={errorId}>{data.message}</Error>}
          />
        )}
      </Wrapper>
    );
  }
);

TextInput.defaultProps = {
  component: 'input',
};

export default TextInput;
